import { Box, Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import { Item } from "../../styles/common";
import React, { useCallback } from "react";
import { useQuery } from "@apollo/client";
import { GET_USER_ACTIVITY } from "./userActivity.queries";
import { Filter, UserFilters } from "../users/UserFilters";
import { UserActivityFilter, UserActivityFilterData } from "./UserActivityFilter";
import { Table } from "../../common/table/Table";
import { DownloadCsvButton } from "../../common/DownloadCsvButton";
import { config } from "../../config";

const perPage = config.rowsPerPage;

export const UserActivity: React.FC<any> = (): JSX.Element => {
  let variables = React.useRef<any>({
    "pagination": {
      "skip": 0,
      "take": perPage
    },
    "activityfilter": {
      "filters": [
        {
          "field": "createdAt",
          "operator": ">=",
          "value": new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        }
      ],
      "operator": "AND"
    }
  });

  const { data, loading, refetch } = useQuery(GET_USER_ACTIVITY, { variables: variables.current });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(perPage);
  const [uniqueResult, setUniqueResult] = React.useState(false);

  React.useEffect(() => {
    variables.current = {
      ...variables.current,
      uniqueResult,
      pagination: {
        take: rowsPerPage,
        skip: page * rowsPerPage,
      },
    };
    refetch(variables.current);
  }, [page, rowsPerPage, uniqueResult, refetch]);

  const onFilter = useCallback((filter: Filter) => {
    variables.current = {
      ...variables.current,
      filter: {
        fieldFilter: {
          filters: filter.filters,
          operator: filter.operator,
        },
        hasReferred: filter.usersThatHaveReferred,
        hasBeenReferred: filter.usersThatHaveBeenReferred,
        hasRecommendedABusiness: filter.usersThatHaveRecommended,
        hasRecommendedSameBusiness: filter.usersThatHaveRecommendedSameBusiness,
        visitedPage: filter.visitedPage,
        hasSharedReferralLink: filter.hasSharedReferralLink,
        referredByUser: filter.referredByUser,
      },
    };
    refetch(variables.current);
  }, [refetch]);

  const onActivityFilter = useCallback((filter: UserActivityFilterData) => {
    variables.current = {
      ...variables.current,
      activityfilter: {
        filters: filter.filters,
        operator: filter.operator,
      },
    };
    refetch(variables.current);
  }, [refetch]);

  if (loading || !data.getUserFlows) return <h4>Loading...</h4>;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Item>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={{ m: 2 }} variant="h4">User Activity</Typography>
            </Grid>
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item>
            <h3>User filter</h3>
            <UserFilters onFilter={onFilter} />
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item>
            <h3>User activity filter</h3>
            <UserActivityFilter onFilter={onActivityFilter} />
            <div>
              <FormControlLabel
                label="Unique users"
                control={
                  <Checkbox
                    checked={uniqueResult}
                    onChange={(e: any) => {
                      setUniqueResult(!uniqueResult);
                    }}
                  />
                }
              />
            </div>
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item>
            <DownloadCsvButton
              path="user/export-user-activity-csv"
              filter={{ filter: variables.current.filter, activityFilter: variables.current.activityfilter, uniqueResult }}
            />
            <Table
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              loading={loading}
              columns={[
                { label: 'First Name', key: 'firstName' },
                { label: 'Last Name', key: 'lastName' },
                { label: 'Username', key: 'username' },
                ...uniqueResult ?
                  [] :
                  [
                    { label: 'Region City', key: 'regionCity' },
                    { label: 'Region State', key: 'regionState' },
                    { label: 'Region Country', key: 'regionCountry' },
                  ],
                {
                  label: 'Type', key: 'type', rowRender: (row) => {
                    switch (row.type) {
                      case 'app-startup':
                        return 'Opened the app';
                      case 'screen_view':
                        return 'Viewed screen';
                      case 'business_recommendation':
                        return 'Recommended a business';
                      case 'share_invitation_link':
                        return 'Shared invitation link';
                      case 'reply_to_message':
                        return 'Replied to a message in teams';
                      case 'message_sent':
                        return 'Sent a message in a team';
                      case 'referred_with_recommendation_points':
                        return 'Referred user recommended a business';
                      default:
                        return row.type;
                    }
                  }
                },
                { label: 'Parameters', key: 'parameters' },
                ...uniqueResult ?
                  [] as any :
                  [{ label: 'Created At', key: 'createdAt', textAlign: 'right', rowRender: (row: any) => new Date(row.createdAt).toLocaleString() }],
              ]}
              rows={data.getUserFlows.userActivities}
              total={data.getUserFlows.total}
            />
          </Item>
        </Grid>
      </Grid>
    </Box>
  );
};